<script setup lang="ts">
import { ContentComp, DropdownSelect, InputDropdownCheckboxes, ListComp } from '@/components';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import { useLoaders, useTaskLoader } from '@/composeables';
import type { SelectLine, SelectLineValueType } from '@/objects';
import type { GPIOReading } from '@/objects/generated';
import type ListCompExpose from '@/objects/ListCompExpose';
import { useEventCommonStore, useContestantStore, useEventStore, useGuiStore, useGPIStore } from '@/stores';
import { computed, ref, toRaw, watch } from 'vue';
import GPIOReadingUsedByCellRenderer from './GPIOReadingUsedByCellRenderer.vue';
import DisplayBool from '@/components/Basic/DisplayBool.vue';
import GPIOReadingUsedForCellRenderer from './GPIOReadingUsedForCellRenderer.vue';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { useDebouncer } from '@/utilities/Debouncer';
import type { IGridRowCssRule } from '@/components/Grid/Classes/IGridRowCssRule';

const props = withDefaults(
    defineProps<{
        /**
         * The station to filter on.
         */
        station?: 'all' | 'start' | 'finish' | number;
    }>(),
    {
        station: 'all',
    },
);

const emit = defineEmits<{
    (e: 'selectedValue', selectedRows: GPIOReading[]): void;
}>();

// The selected rows in the grid
const internalSelectedRows = ref<GPIOReading[]>([]);

// Emit the selected rows when they change
watch(internalSelectedRows, (value) => emit('selectedValue', value));

// The list component
const listComp = ref<ListCompExpose<GPIOReading> | null>(null);

// Current event
const { eventID } = useEventStore();
const gpistore = useGPIStore();
const comStore = useEventCommonStore();
const contestantStore = useContestantStore();

// The list of readings
const list = ref<Array<GPIOReading>>([]);

// The actual list of readings that we show in the grid after filtering and grouping
const filteredList = ref<Array<GPIOReading>>([]);

const showAsModal = ref<boolean>(false);

// Setup the loader and start loading initial data
const { AddLoader, LoadState } = useTaskLoader();
AddLoader(async () => {
    await useLoaders(AddLoader, ['STATIONS', 'CONTESTANTS']);
    await fetchData();

    showAsModal.value = true;
}, 'gpioreadings.loading');

/**
 * Handle the list and applying filter and grouping rules
 */
const filterAndGroupList = () => {
    let _filtered = toRaw(list.value);

    // Filter on selected ports
    if (selectedPorts.value.length > 0) {
        _filtered = _filtered.filter((item) => selectedPorts.value.includes(item.Port));
    }

    if (selectedTimePeriodInSeconds.value > 0) {
        let existingTime = new Set<string>();
        let dateOffset = new Date(new Date().getTime() - selectedTimePeriodInSeconds.value * 1000);
        _filtered = _filtered.filter((item) => {
            let time = item.Time.toISOString().replace('Z', '');
            let timeStringGrouped = time.substring(0, time.length - decimalGrouping.value) + item.Port;

            if (item.Time > dateOffset && item.Time <= new Date() && !existingTime.has(timeStringGrouped)) {
                existingTime.add(timeStringGrouped);
                return true;
            }
        });
    }

    // Sort filtered list by Time desc
    _filtered.sort((a, b) => {
        return b.Time.getTime() - a.Time.getTime();
    });

    filteredList.value = _filtered;

    listComp.value?.dataUpdated();
};

/**
 * Return a list of the uniuqe ports in the list so we can filter on them
 */
const computedPortsDatasource = computed(() => {
    let ports = new Set<number>();
    list.value.forEach((x) => ports.add(x.Port));

    let _list = Array.from(ports).map((x) => {
        return { value: x, text: x.toString() };
    });

    return _list.sort((a, b) => {
        return a.value - b.value;
    }) as Array<SelectLine>;
});

// Helper property for when we load the first time
let initialLoad = true;

// This contains the rowIds that have changed in the list
// This is a bit of a hack to highlight the rows that have changed since we dont get any information from the server about what rows have changed. So we compare the rowIds in the list before and after the fetch
let changedRowIds: string[] = [];

/**
 * Fetch the data from the backend based on what station to filter on.
 */
const fetchData = async () => {
    AddLoader(async () => {
        // Create a list of all the rowIds we have in the list
        let rowIds = list.value.map((x) => x.Id);
        await gpistore.EnsureLoaded();
        list.value = gpistore.GetGPIReadings();

        changedRowIds = list.value
            .map((x) => x.Id)
            .filter((x) => !rowIds.includes(x))
            .filter((x) => x != undefined);
    }, 'gpioreadings.loading');
};

const selectedPorts = ref<SelectLineValueType[]>([]);

const selectedDeviceTypes = ref<SelectLineValueType[]>([]);

//  The timePeriod filter
const selectedTimePeriodInSeconds = ref<number>(0);

// The decimal grouping filter we default o grouping by seconds
const decimalGrouping = ref<number>(3);

/**
 * Add custom columns to the grid
 * @param columns
 */
const addCustomColumns = (columns: GridColumnOptions<GPIOReading>[]) => {
    // // add a new column to the list on the third postion

    new GridColumnOptionsBuilder<GPIOReading>('High', 'gpioreading.high').ReadOnly().CellRenderer(DisplayBool).InsertAtEnd(columns);

    new GridColumnOptionsBuilder<GPIOReading>('UsedFor', 'gpioreading.usedfor', 'usedfor')
        .ReadOnly()
        .CellRenderer(GPIOReadingUsedForCellRenderer)
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<GPIOReading>('RaceContestantIDs', 'gpioreading.usedby', 'usedby')
        .ReadOnly()
        .Size(350)
        .CellRenderer(GPIOReadingUsedByCellRenderer)
        .InsertAtEnd(columns);

    return columns;
};

/**
 * We need a custom filter in order to filter on the used by field
 * @param reading
 * @param query
 */
const customFilter = (reading: GPIOReading, query: string) => {
    if (query == '') {
        return true;
    }

    // We need to check the used by field
    if (query.length > 0 && reading.RaceContestantIDs.length > 0) {
        for (const usedByRaceContestantID of reading.RaceContestantIDs) {
            // we create a list of displaynames for this used by
            let displayName = contestantStore.GetDisplayName(usedByRaceContestantID)?.toLocaleLowerCase();

            if (displayName.includes(query.toLowerCase())) {
                return true;
            }
        }
    }

    return false;
};

/**
 * We set up the signalR subscription for the GPI readings
 */
const debounce = useDebouncer();
signalrSubscribe.GPIOReading({ EventID: eventID }, (gpiReadings: GPIOReading[]) => {
    initialLoad = false;
    debounce(fetchData, 1000);
    // TODO: We should actually get the complete object from the server so we can just add it to the list instead of refetching the whole list
});

/**
 * The row class rules for the grid
 */
const rowClassRules: IGridRowCssRule<GPIOReading>[] = [
    {
        className: 'grid-row-highlight',
        func: (data: GPIOReading) => !initialLoad && changedRowIds.includes(data.Id ?? ''),
    },
];

/**
 * We change the content of the list when the selected time period changes or the decimal grouping changes
 */
watch([selectedTimePeriodInSeconds, decimalGrouping, list, selectedPorts], filterAndGroupList);
</script>

<template>
    <ContentComp
        :load-state="LoadState"
        :show-as-modal="showAsModal"
        :quiet-load="true"
        class="h-100"
    >
        <ListComp
            ref="listComp"
            v-model="filteredList"
            @on-selection-changed="
                (rows: GPIOReading[]) => {
                    internalSelectedRows = rows;
                }
            "
            :gui="useGuiStore().GPIOReading"
            :hide-default-buttons="true"
            :use-default-menu-actions="false"
            :select-all="true"
            :readonly="true"
            :filter="[customFilter]"
            :custom-columns="addCustomColumns"
            rowselection="single"
            :row-class-rules="rowClassRules"
            @refresh-list="
                () => {
                    initialLoad = true;
                    fetchData();
                }
            "
            ><template #menu-filters>
                <InputDropdownCheckboxes
                    v-if="false"
                    icon="fa-filter"
                    v-model="selectedDeviceTypes"
                    :data="comStore.StationsAsSelectLine"
                    ><span class="ms-2">{{ $tm('readings.stations') }}</span></InputDropdownCheckboxes
                >
                <DropdownSelect
                    v-model="selectedTimePeriodInSeconds"
                    icon="fa-filter"
                    caption="general.timeperiod"
                    :keep-caption="true"
                    :data="[
                        { value: 0, text: 'general.all' },
                        { value: 300, text: 'time.5minutes' },
                        { value: 600, text: 'time.10minutes' },
                        { value: 1800, text: 'time.30minutes' },
                        { value: 3600, text: 'time.60minutes' },
                    ]"
                />

                <InputDropdownCheckboxes
                    v-model="selectedPorts"
                    :data="computedPortsDatasource"
                    icon="fa-filter"
                    ><span class="ms-2">{{ $tm('readings.ports') }}</span></InputDropdownCheckboxes
                >
            </template>
        </ListComp>
    </ContentComp>
</template>
